import { QRCodeSVG } from "qrcode.react";
import { QrCode } from "lucide-react";
import { PageLayout } from "@/ui/components/layout/PageLayout";
import { Card, CardContent } from "@/ui/components/ui/card";
import { useApiTokenObject } from "@/state/store/selectors/auth";
import type { JwtPayload } from "@/state/store/types";
import { normalizeFullHandle } from "@/features/contacts/handleUtils";

type HandleSource = Partial<Pick<JwtPayload, "full_handle" | "username" | "discriminator">> & {
  fullHandle?: string | null;
};

export function resolveCanonicalHandle(claims: HandleSource): string | null {
  const candidateHandles = [claims.full_handle, claims.fullHandle];
  for (const candidate of candidateHandles) {
    const normalized = candidate ? normalizeFullHandle(candidate) : null;
    if (normalized) {
      return normalized;
    }
  }

  if (!claims.username || !claims.discriminator) {
    return null;
  }

  return normalizeFullHandle(`${claims.username}.${claims.discriminator}`);
}

type ShowMyQrContentProps = {
  fullHandle?: string | null;
  username?: string | null;
  discriminator?: string | null;
};

export const ShowMyQrContent = ({
  fullHandle,
  username,
  discriminator,
}: ShowMyQrContentProps = {}) => {
  const claims = useApiTokenObject();
  const canonicalHandle =
    resolveCanonicalHandle({ fullHandle, username, discriminator }) ||
    resolveCanonicalHandle(claims);

  return (
    <Card data-name="show-my-qr-content">
      <CardContent className="pt-6 space-y-6">
        <div className="space-y-1 text-center">
          <h2 className="text-xl font-semibold">Let someone scan your handle</h2>
          <p className="text-sm text-muted-foreground">
            This QR code contains your full handle for the existing add-contact flow.
          </p>
        </div>

        {canonicalHandle ? (
          <div className="space-y-4">
            <div className="mx-auto w-fit rounded-2xl border bg-white p-4 shadow-sm">
              <QRCodeSVG value={canonicalHandle} size={240} level="M" includeMargin />
            </div>
            <p className="text-center font-mono text-base font-medium">
              {canonicalHandle}
            </p>
          </div>
        ) : (
          <div className="flex min-h-80 flex-col items-center justify-center gap-3 rounded-2xl border bg-muted/30 px-6 text-center">
            <QrCode className="h-10 w-10 text-muted-foreground" />
            <p className="max-w-sm text-sm text-muted-foreground">
              Your account handle is not available right now. Try again later.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const ShowMyQrPane = () => (
  <div data-name="show-my-qr-pane" className="h-full overflow-auto p-4">
    <ShowMyQrContent />
  </div>
);

const ShowMyQrScreen = () => {
  return (
    <PageLayout title="Show My QR" showBack>
      <div className="p-4 space-y-4">
        <ShowMyQrContent />
      </div>
    </PageLayout>
  );
};

export default ShowMyQrScreen;
